import { useMemo, type JSX } from 'react';
import { Banknote, Gift, HandCoins, Wallet } from 'lucide-react';
import { useAdminNiches } from '@/hooks/useAdmin';
import { useSpend } from '@/hooks/useInsights';
import { useUrlState } from '@/hooks/useUrlState';
import { NO_NICHE, UNATTRIBUTED, type AgingBucket, type CostPerRow, type EarnedComponent, type SpendResponse } from '@/api/insights';
import { formatDate, formatWeekRange } from '@/lib/dates';
import {
  changeOf,
  compactMoney,
  insightsInvitesHref,
  JOINS_DATED_FROM,
  money,
  pctText,
  shareOf,
  toNum,
  windowPredatesJoinDates,
} from '@/lib/insights';
import { creatorTypeLabel } from '@/lib/labels';
import { useInsightsScope } from './scope';
import {
  BarList,
  KpiTile,
  LoadError,
  Loading,
  MetricLabel,
  Note,
  Panel,
  Section,
  Segmented,
  Th,
  Unavailable,
} from './parts';

type CostBy = 'team' | 'creatorType' | 'niche';

const COST_BY: { value: CostBy; label: string }[] = [
  { value: 'team', label: 'Team' },
  { value: 'creatorType', label: 'Creator type' },
  { value: 'niche', label: 'Niche' },
];

const COMPONENT_LABELS: Record<EarnedComponent, string> = {
  log: 'Reply logged',
  join: 'Creator joined',
  post: 'Approved posts',
  bracket: 'Follower bonus',
  niche: 'Niche bonus',
  other: 'Other (adjustments)',
};

const BUCKET_LABELS: Record<AgingBucket, string> = {
  '0-7': 'Up to a week',
  '8-30': '8 to 30 days',
  '31-90': '31 to 90 days',
  '90+': 'Over 90 days',
};

const FALLBACK_DEFS: Record<string, string> = {
  cashSpent: 'Payouts marked paid in this period, including prizes, retainers and top-ups.',
  inviteEarningsPaid: 'The part of paid payouts that settled invite earnings.',
  additionalPaid: 'Prizes, retainers and top-ups paid on top of invite earnings.',
  earnedByComponent:
    'What invites logged in this period have earned so far, by what earned it. Current earned value; re-priced daily until SPA-1207 ships.',
  costPerJoin: 'Cash spent ÷ joins recorded in this period.',
  costPerPost: 'Cash spent ÷ first posts recorded in this period.',
  liabilityAging:
    'What the ledger says is owed right now, by how long ago the invite was logged. Payroll’s owed figure uses an older formula, so the two can differ.',
  unattributed: 'Cash not tied to any invite: prizes, retainers and top-ups, and payouts with no invite events behind them.',
  beforeTrackingAllTime: 'Paid before payments were tracked invite by invite; imported as one historical total.',
};

export function SpendTab(): JSX.Element {
  const scope = useInsightsScope();
  const [url, setUrl] = useUrlState({ costBy: 'team' });
  const costBy: CostBy = COST_BY.some((c) => c.value === url.costBy) ? (url.costBy as CostBy) : 'team';
  const q = useSpend(scope.params);
  const prev = useSpend(scope.previousParams);

  if (q.isLoading) return <Loading label="Loading spend…" />;
  if (q.isError || !q.data) return <LoadError error={q.error} what="spend" />;

  const d = q.data;
  const def = (key: string): string | undefined => d.definitions?.[key] ?? FALLBACK_DEFS[key];
  const p = prev.data?.cash ?? null;
  const owedTotal = d.liabilityAging.reduce((s, b) => s + (toNum(b.owed) ?? 0), 0);
  const owedInvites = d.liabilityAging.reduce((s, b) => s + b.invites, 0);
  const team = scope.team || undefined;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          icon={Banknote}
          label="Cash paid"
          def={def('cashSpent')}
          value={money(d.cash.total)}
          to="/admin/payouts?status=PAID"
          change={p ? changeOf(toNum(d.cash.total), toNum(p.total), { kind: 'money' }) : null}
        />
        <KpiTile
          icon={HandCoins}
          label="Invite earnings"
          def={def('inviteEarningsPaid')}
          value={money(d.cash.inviteEarnings)}
          sub={shareText(d.cash.inviteEarnings, d.cash.total)}
        />
        <KpiTile
          icon={Gift}
          label="Additional"
          def={def('additionalPaid')}
          value={money(d.cash.additional)}
          sub={`prizes, retainers, top-ups · ${shareText(d.cash.additional, d.cash.total)}`}
        />
        <KpiTile
          icon={Wallet}
          label="Owed now (ledger)"
          def={def('liabilityAging')}
          value={money(owedTotal)}
          to={owedTotal > 0 ? insightsInvitesHref({ team, owed: true }) : null}
          sub={`on ${owedInvites.toLocaleString()} invite${owedInvites === 1 ? '' : 's'}, right now`}
        />
      </div>

      <WeeklyCash weekly={d.weekly} />

      <div className="grid gap-6 lg:grid-cols-2">
        <Section
          title="What is the money being earned for?"
          description={
            <>
              <MetricLabel label="Earned so far" def={def('earnedByComponent')} /> by invites logged in this period, by
              what earned it. This is accrued value, not cash paid.
            </>
          }
        >
          <EarnedByComponent rows={d.earnedByComponent} />
        </Section>
        <Section
          title="How long has what we owe been waiting?"
          description={<MetricLabel label="Owed right now, by how old the invite is" def={def('liabilityAging')} />}
        >
          <Panel>
            {owedTotal === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing is owed right now.</p>
            ) : (
              <BarList
                ariaLabel="Owed by invite age"
                rows={d.liabilityAging.map((b) => ({
                  key: b.bucket,
                  label: BUCKET_LABELS[b.bucket] ?? b.bucket,
                  value: toNum(b.owed) ?? 0,
                  display: money(b.owed),
                  sub: `${b.invites.toLocaleString()} invite${b.invites === 1 ? '' : 's'}`,
                  tone: b.bucket === '31-90' || b.bucket === '90+' ? 'warning' : 'brand',
                }))}
              />
            )}
          </Panel>
        </Section>
      </div>

      <CostPerTable costPer={d.costPer} costBy={costBy} onCostBy={(v) => setUrl({ costBy: v })} def={def} />

      <Note>
        <MetricLabel label="Before tracking" def={def('beforeTrackingAllTime')} />: {money(d.cash.beforeTrackingAllTime)}{' '}
        was paid before payments were tracked invite by invite. It is an all-time total, so it isn’t in any period
        above.
      </Note>
    </div>
  );
}

/** Labels for the backend's catch-all groups: "(unattributed)", "(none)". */
function groupLabel(code: string, name: (code: string) => string): string {
  if (code === UNATTRIBUTED) return 'Not tied to an invite';
  if (code === NO_NICHE || !code) return 'No niche';
  return name(code);
}

function shareText(part: SpendResponse['cash']['total'], total: SpendResponse['cash']['total']): string {
  const s = shareOf(toNum(part) ?? 0, toNum(total) ?? 0);
  return s === null ? 'nothing paid' : `${pctText(s)} of cash paid`;
}

// ── Weekly cash ─────────────────────────────────────────────────────────────

function WeeklyCash({ weekly }: { weekly: SpendResponse['weekly'] }): JSX.Element {
  const rows = weekly.map((w) => ({
    week: w.weekStart,
    invite: Math.max(0, toNum(w.inviteEarnings) ?? 0),
    additional: Math.max(0, toNum(w.additional) ?? 0),
  }));
  const max = Math.max(...rows.map((r) => r.invite + r.additional), 0);
  const total = rows.reduce((s, r) => s + r.invite + r.additional, 0);
  const additional = rows.reduce((s, r) => s + r.additional, 0);
  const width = 640;
  const height = 160;
  const slot = width / Math.max(rows.length, 1);
  const barW = Math.max(3, slot * 0.62);
  const scale = (v: number): number => (max > 0 ? (v / max) * (height - 8) : 0);

  return (
    <Section
      title="Where did the cash go each week?"
      description={
        total > 0
          ? `Cash paid per week, split into invite earnings and additional pay (prizes, retainers, top-ups). Additional was ${pctText(shareOf(additional, total))} of the total.`
          : 'Cash paid per week, split into invite earnings and additional pay (prizes, retainers, top-ups).'
      }
    >
      <Panel>
        {rows.length === 0 || total === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing was paid in this period.</p>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-brand" aria-hidden /> Invite earnings
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-warning" aria-hidden /> Additional
              </span>
              <span className="ml-auto font-mono">Tallest week {compactMoney(max)}</span>
            </div>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              preserveAspectRatio="none"
              className="block h-40 w-full"
              role="img"
              aria-label={`Cash paid per week. ${rows
                .map((r) => `${formatWeekRange(r.week)}: ${money(r.invite)} invite earnings, ${money(r.additional)} additional`)
                .join('; ')}`}
            >
              <line x1={0} x2={width} y1={height - 0.5} y2={height - 0.5} className="stroke-border" strokeWidth={1} />
              {rows.map((r, i) => {
                const x = i * slot + (slot - barW) / 2;
                const hi = scale(r.invite);
                const ha = scale(r.additional);
                return (
                  <g key={r.week}>
                    <title>{`${formatWeekRange(r.week)}: ${money(r.invite)} invite earnings + ${money(r.additional)} additional`}</title>
                    <rect x={x} y={height - 1 - hi} width={barW} height={hi} className="fill-brand" />
                    <rect x={x} y={height - 1 - hi - ha} width={barW} height={ha} className="fill-warning" />
                  </g>
                );
              })}
            </svg>
            <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
              <span>Week of {formatDate(`${rows[0]!.week.slice(0, 10)}T12:00:00Z`)}</span>
              <span>Week of {formatDate(`${rows[rows.length - 1]!.week.slice(0, 10)}T12:00:00Z`)}</span>
            </div>
          </>
        )}
      </Panel>
    </Section>
  );
}

// ── Earned by component ─────────────────────────────────────────────────────

function EarnedByComponent({ rows }: { rows: SpendResponse['earnedByComponent'] }): JSX.Element {
  const total = rows.reduce((s, r) => s + (toNum(r.amount) ?? 0), 0);
  const sorted = [...rows].sort((a, b) => (toNum(b.amount) ?? 0) - (toNum(a.amount) ?? 0));
  return (
    <Panel>
      {total === 0 ? (
        <p className="text-sm text-muted-foreground">Invites logged in this period haven’t earned anything yet.</p>
      ) : (
        <BarList
          ariaLabel="Earned by component"
          rows={sorted.map((r) => ({
            key: r.component,
            label: COMPONENT_LABELS[r.component] ?? r.component,
            value: Math.max(0, toNum(r.amount) ?? 0),
            display: money(r.amount),
            sub: pctText(shareOf(toNum(r.amount) ?? 0, total)),
          }))}
        />
      )}
    </Panel>
  );
}

// ── Cost per join / post ────────────────────────────────────────────────────

function CostPerTable({
  costPer,
  costBy,
  onCostBy,
  def,
}: {
  costPer: SpendResponse['costPer'];
  costBy: CostBy;
  onCostBy: (v: CostBy) => void;
  def: (key: string) => string | undefined;
}): JSX.Element {
  const scope = useInsightsScope();
  const niches = useAdminNiches();
  const nicheName = useMemo(() => {
    const map = new Map((niches.data ?? []).map((n) => [n.code, n.displayName]));
    return (code: string) => map.get(code) ?? code;
  }, [niches.data]);

  const rows: (CostPerRow & { key: string; label: string })[] = useMemo(() => {
    const list =
      costBy === 'team'
        ? costPer.byTeam.map((r) => ({ ...r, key: r.team, label: scope.teamName(r.team) }))
        : costBy === 'creatorType'
          ? costPer.byCreatorType.map((r) => ({ ...r, key: r.creatorType, label: groupLabel(r.creatorType, creatorTypeLabel) }))
          : costPer.byNiche.map((r) => ({ ...r, key: r.niche, label: groupLabel(r.niche, nicheName) }));
    // Cheapest post first; groups with no posts (no cost) last, by cash.
    return [...list].sort((a, b) => {
      const ca = toNum(a.costPerPost);
      const cb = toNum(b.costPerPost);
      if (ca === null && cb === null) return (toNum(b.cash) ?? 0) - (toNum(a.cash) ?? 0);
      if (ca === null) return 1;
      if (cb === null) return -1;
      return ca - cb;
    });
  }, [costBy, costPer, scope, nicheName]);
  const noun = COST_BY.find((c) => c.value === costBy)!.label;
  const predates = windowPredatesJoinDates(scope.windows.current);
  const noJoinsReason = predates
    ? `No dated joins: joins are only recorded with a date from ${formatDate(`${JOINS_DATED_FROM}T12:00:00Z`)}`
    : 'No joins recorded in this period';

  return (
    <Section
      title="Where does a post cost least?"
      description={
        <>
          <MetricLabel label="Cash spent in the period" def={def('costPerGroup')} /> ÷ joins and first posts recorded in
          the period, cheapest post first.
        </>
      }
      actions={<Segmented label="Group cost by" value={costBy} options={COST_BY} onChange={onCostBy} />}
    >
      {rows.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted-foreground">No cash was spent in this period.</p>
        </Panel>
      ) : (
        <div className="table-frame relative overflow-x-auto">
          <table className="responsive-stack w-full text-sm" aria-label={`Cost per post by ${noun.toLowerCase()}`}>
            <thead>
              <tr className="text-left">
                <Th label={noun} numeric={false} />
                <Th label="Cash spent" def={def('cashSpent')} />
                <Th label="Joins" />
                <Th label="Posts" />
                <Th label="Cost per join" def={def('costPerJoin')} />
                <Th label="Cost per post" def={def('costPerPost')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key || '(none)'} className="border-t border-border transition-colors hover:bg-accent/40">
                  <td className="px-3 py-2 font-medium">
                    {r.key === UNATTRIBUTED ? (
                      <MetricLabel label={r.label} def={def('unattributed')} className="text-muted-foreground" />
                    ) : (
                      r.label
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Cash spent">
                    {money(r.cash)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Joins">
                    {r.joined.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Posts">
                    {r.posted.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Cost per join">
                    {r.costPerJoin == null ? <Unavailable reason={noJoinsReason} /> : money(r.costPerJoin)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Cost per post">
                    {r.costPerPost == null ? <Unavailable reason="No posts recorded in this period" /> : money(r.costPerPost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {predates ? (
        <Note tone="warning">
          Joins are only dated from {formatDate(`${JOINS_DATED_FROM}T12:00:00Z`)}, so cost per join can’t be worked
          out before then (shown as “—”) and reads high for a period that starts earlier.
        </Note>
      ) : null}
    </Section>
  );
}
